import React, { useEffect, useState } from "react";
import {
  Heading,
  Flex,
  Box,
  Spinner,
  Button,
  Container, Grid, useToast
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";

import Layout from "@pages/Layout";
import { Separator, View, CandidateItem, BoxData, CustomSkeleton } from "@components";
import { applicantsListing, loadAnnonceDetails } from "@lib/datahandlers/company";
import { t } from "@lang/i18n";
import { PER_PAGE } from "@lib/constants";
import { AuthContext } from "@contexts/AuthContext";

const AnnonceDetailAttr = ({ field, data }) => {
  return(
    <Box mb="16px" >
      <Box as="span" fontWeight="400" textColor="black" fontSize="14px">
          {field} : 
      </Box>
      <BoxData data={data} />
    </Box>
  )
};


const AnnonceDetails = () => {
  const initStateLoadMore = { page: 1, has_more: "", loading_more: "" };

  const [applicants, setApplicants] = useState([]);
  const [annonce, setAnnonce] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadMore, setLoadMore] = useState(initStateLoadMore);

    // get user id
    const { id } = useParams();
    const toast = useToast();

    // check if obj is empty
    const isObjectEmpty = (obj) => {
        if (Object.entries(obj).length === 0) return true;
        return false;
    };
    
    // load applicants
    const onLoadApplicants = async (page, id) => {
      setIsLoaded(true);
      const result = await applicantsListing(page, id);

      if(result.success) {
        setApplicants(result.data.persons);
        setLoadMore({
          ...loadMore,
          has_more: result.data.length === PER_PAGE,
        });
        setIsLoaded(false);
      }
      // else toast({description: t('global.general_error'), status: "warning", variant: 'subtle', duration: 3000});
    };
  
    // load annonce details
    const onLoadDetails = async (id) => {
      const result = await loadAnnonceDetails(id);
      if (result.success) {
        const { address, nbr_positions, salary_min, salary_max, contracts, experiences, fonctions } = result.data;
        setAnnonce({
            address,
            nbr_positions,
            salary_min,
            salary_max,
            fonctions: fonctions.length > 0 && fonctions.map(el => el.name),
            contracts: contracts.length > 0 && contracts.map(el => el.name),
            experiences: experiences.length > 0 && experiences.map(el => el.name)
        });
        return;
      }
    };

    // show more applicants
    const showMore = async () => {
      let nextPage = loadMore.page + 1;

      setLoadMore({...loadMore, loading_more: true})
      const result = await await applicantsListing(id, nextPage);
      setLoadMore({...loadMore, loading_more: false})

      if(result.success) {
        setLoadMore({...loadMore,
            has_more: result.data.length === PER_PAGE,
            page: nextPage,
        });
        setApplicants({
          applicants: [...applicants, ...result.data],
        });
      }
      else toast({description: t('global.general_error'), status: "warning", variant: 'subtle', duration: 3000});
    };

    // load annonce details
    useEffect(() => {
      if (isObjectEmpty(annonce)) {
          onLoadDetails(id);
      }
    }, []);

    // laod applicants
    useEffect(() => {
        if (applicants.length === 0) {
          onLoadApplicants(loadMore.page, id);
        }
    }, []);
  
  return (
    <Layout menuType="company" isNavBarVisible isFooterVisible>
      <Container maxW="1024px" bg="white" minHeight='calc(100vh - 100px)' py="36px" px={["16px","","","100px"]} borderRadius="4px">
        <Flex flexDir="column">

        <Heading as="h1" fontSize="30px" textColor="black" textAlign="center">
          {t("company.annonce.details_annonce.title")}
        </Heading>

        <Separator />

        {/* Annonce Details */}
        <View if={annonce.fonctions} fontWeight="600" textColor="black" ml=".2rem" mb="10px">
          { Array.isArray(annonce.fonctions) ? annonce.fonctions.map(el => el).join(', ') : annonce.fonctions }
        </View>

        <Grid templateRows="repeat(2, 1fr)" rowGap="16px" placeItems={["center", "initial", "", ""]}>
          <Grid templateColumns="repeat(auto-fit, minmax(150px, 1fr))" placeItems="self-start" columnGap="50px">
            <AnnonceDetailAttr field={t("company.annonce.details_annonce.number_position")} data={annonce.nbr_positions} />
            <AnnonceDetailAttr field={t("company.annonce.details_annonce.experience")} 
                data={annonce.experiences && annonce.experiences.map(el => el).join(', ')} />
            <AnnonceDetailAttr field={t("fields.company.address")} data={annonce.address} />
          </Grid>
          <Grid templateColumns="repeat(auto-fit, minmax(150px, 1fr))" placeItems="self-start" columnGap="50px">
            <AnnonceDetailAttr field={t("company.annonce.details_annonce.contracts")} 
              data={annonce.contracts && annonce.contracts.map(el => el).join(', ')} />
            <AnnonceDetailAttr field={t("company.annonce.details_annonce.salary_yearly_min")} data={annonce.salary_min} />
            <AnnonceDetailAttr field={t("company.annonce.details_annonce.salary_yearly_max")} data={annonce.salary_max} />
          </Grid>
        </Grid>


        {/* Annonces listing */}
        <View if={!isLoaded} m="24px 5px 24px 0px">
          {t("company.annonce.details_annonce.total_apply")} : <Box as="span" fontSize="16px" fontWeight="600"> {applicants.length}  </Box>
        </View>

        {/* skeleton loading */}
        <View if={isLoaded} display="flex" flexDir="row" flexWrap="wrap" justifyContent={{base: "center", lg: "space-between"}} mt="49px">
          <CustomSkeleton type="FETCH_ITEMS" />
          <CustomSkeleton type="FETCH_ITEMS" />
          <CustomSkeleton type="FETCH_ITEMS" />
        </View>

        {/* Listing applicants */}
        <View if={!isLoaded && applicants && applicants.length > 0} display="flex" flexDir="row" flexWrap="wrap" justifyContent={{base: "center", lg: "space-between"}}>
          { applicants.map((el, idx) => 
            <CandidateItem key={idx} data={el} id={el.id} iconType="company" candidates={applicants} setCandidates={setApplicants} />) 
          }
        </View>
        

        {/* Pagination */}
        <View if={loadMore.has_more} flexBasis="100%" textAlign="center">
          <Button size="xs" color="gray.light" fontWeight="normal" m="40 auto 0 auto" variant="ghost"
              _hover={{ textDecor: "underline" }} onClick={() => showMore()}>
              { loadMore.loading_more? <Spinner size="sm" thickness="3px" color="gray.light" /> : t('candidate.search_job.main.show_more') }
          </Button>
        </View>
          
        </Flex>
      </Container>
    </Layout>
  );
};
export default AnnonceDetails;